import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useProvince } from "@/contexts/ProvinceContext";
import { Cross, Phone, Clock, Map as MapIcon } from "lucide-react";

function mapUrl(p, provinceName) {
  const parts = [p?.name, p?.address, p?.district, provinceName, "Türkiye"].filter(Boolean);
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(parts.join(", "))}`;
}

export default function PharmaciesBlock() {
  const { selected } = useProvince();
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ is_stale: false, stale_days: 0, on_duty_date: null });

  useEffect(() => {
    if (!selected?.id) return;
    let cancelled = false;
    api
      .get(`/pharmacies/current?province_id=${selected.id}`)
      .then((r) => {
        if (cancelled) return;
        setItems(r.data?.items || []);
        setMeta({
          is_stale: !!r.data?.is_stale,
          stale_days: r.data?.stale_days || 0,
          on_duty_date: r.data?.on_duty_date || null,
        });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [selected?.id]);

  const shiftLabel = useMemo(() => {
    const withLabel = items.find((p) => p.on_duty_label);
    return withLabel?.on_duty_label || "";
  }, [items]);

  // Always render the block even when empty so admins/visitors see the
  // module exists. Show a helpful empty state when no data is available.
  const friendlyDate = meta.on_duty_date
    ? new Date(meta.on_duty_date).toLocaleDateString("tr-TR", {
        day: "2-digit", month: "short", weekday: "short",
      })
    : "";

  // Treat data older than 30 days as unusable — hide the (potentially
  // misleading) list and show an empty state so visitors don't call
  // pharmacies that were on duty months ago.
  const tooStale = meta.is_stale && meta.stale_days > 30;
  const showEmpty = items.length === 0 || tooStale;

  return (
    <div className="w-full bg-white rounded-lg border border-gray-100 p-5" data-testid="home-pharmacies-block">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 flex-wrap">
          <Cross className="w-5 h-5 text-[#e11d2a]" />
          <h3 className="text-lg font-black">NÖBETÇİ ECZANELER</h3>
          {!meta.is_stale && items.length > 0 && (
            <span className="text-[10px] uppercase font-bold tracking-wide bg-[#e11d2a] text-white px-2 py-0.5 rounded">
              Bugün
            </span>
          )}
          {meta.is_stale && !tooStale && (
            <span
              className="text-[10px] uppercase font-bold tracking-wide bg-amber-100 text-amber-800 px-2 py-0.5 rounded"
              title={`Veri ${meta.stale_days} gün eski (${friendlyDate})`}
              data-testid="home-pharmacies-stale"
            >
              {friendlyDate} • {meta.stale_days} gün eski
            </span>
          )}
        </div>
        <Link to="/nobetci-eczaneler" className="text-xs font-semibold text-[#e11d2a] hover:underline">
          Tümünü Gör →
        </Link>
      </div>

      {showEmpty ? (
        <div
          className="border border-dashed border-gray-200 rounded-lg p-5 text-center text-sm text-gray-500"
          data-testid="home-pharmacies-empty"
        >
          {tooStale ? (
            <>
              <div className="font-semibold text-amber-800 mb-1">
                Nöbetçi eczane verisi güncellenemiyor
              </div>
              <div className="text-xs">
                En son bilgi {meta.stale_days} gün önce güncellendi. Lütfen{" "}
                <Link to="/nobetci-eczaneler" className="text-[#e11d2a] font-semibold hover:underline">
                  eczane listesi
                </Link>{" "}
                sayfasından tam listeye göz atın veya 184 Sağlık Danışma Hattı'nı arayın.
              </div>
            </>
          ) : (
            <>Bu il için henüz nöbetçi eczane verisi yok. Yöneticilerin senkronizasyonu tamamlamasını bekleyin.</>
          )}
        </div>
      ) : (
        <>
          {shiftLabel && (
            <div className="flex items-start gap-1.5 text-xs text-gray-600 mb-3" data-testid="home-pharmacies-shift">
              <Clock className="w-3.5 h-3.5 shrink-0 mt-0.5 text-[#e11d2a]" />
              <span>{shiftLabel}</span>
            </div>
          )}

          <div className="space-y-2">
            {items.slice(0, 4).map((p) => (
              <div key={p.id} className="flex items-start justify-between gap-3 border border-gray-100 rounded-lg p-3 hover:border-[#e11d2a]/40">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-semibold text-sm truncate">{p.name}</div>
                    {p.district && (
                      <span className="text-[9px] uppercase font-semibold tracking-wide px-1.5 py-0.5 bg-gray-100 text-gray-700 rounded">
                        {p.district}
                      </span>
                    )}
                  </div>
                  {p.address && <div className="text-xs text-gray-500 mt-0.5 line-clamp-2">{p.address}</div>}
                </div>
                <div className="shrink-0 flex flex-col gap-1.5 items-end">
                  {p.address && (
                    <a
                      href={mapUrl(p, selected?.name)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs font-semibold text-gray-700 flex items-center gap-1 hover:text-[#e11d2a]"
                      title="Haritada göster"
                    >
                      <MapIcon className="w-3.5 h-3.5" /> Harita
                    </a>
                  )}
                  {p.phone && (
                    <a
                      href={`tel:${p.phone}`}
                      className="text-xs font-semibold text-[#e11d2a] flex items-center gap-1 hover:underline"
                    >
                      <Phone className="w-3.5 h-3.5" /> Ara
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
